/**
 * Bunker — runtime limits, shared between client and server.
 *
 * The server's per-route Zod schemas read from these constants, and the SPA
 * mirrors the same values in input components ("up to 4000 chars",
 * "5 messages per 5s slowmode" etc.) so the user sees the same number that
 * the server enforces.
 */
export const LIMITS = Object.freeze({
  guild: {
    perUser: 200,
    channelsPerGuild: 500,
    membersPerGuild: 50_000,
    rolesPerGuild: 250,
    nameMin: 1,
    nameMax: 100,
    /** Guild setting: minutes a new member waits before posting; 0 = off. */
    newMemberWaitMaxMinutes: 1440,
  },
  channel: {
    nameMax: 100,
    topicMax: 1024,
    slowmodeMaxSeconds: 21_600,
  },
  message: {
    contentMax: 4000,
    perUserPerWindow: 5,
    windowMs: 5_000,
    editWindowSeconds: null as number | null,
    /** Distinct reaction emojis per message (Discord's cap): beyond it a new
     *  emoji is refused, existing ones can still be added to. */
    distinctReactionsMax: 20,
    /** Anti-spam (server/src/services/antiSpam.ts). The same text as your
     *  previous message in the same place is refused for this long. */
    repeatWindowMs: 30_000,
    /** A channel taking more than `triggerMessages` in `windowMs` gets a
     *  temporary `slowmodeS` slowmode, held `holdMs` after it last ran hot. */
    autoSlowmode: {
      windowMs: 10_000,
      triggerMessages: 60,
      slowmodeS: 3,
      holdMs: 120_000,
    },
  },
  attachment: {
    // 600 MB for everyone. Uploads go straight to S3 via a presigned PUT, so
    // this drives the client check, the /sign gate and the /complete re-check.
    // NOTE: the S3/minio reverse proxy must allow a matching body size
    // (deploy/native/nginx-minio.snippet.conf → client_max_body_size).
    sizeBytes: 600 * 1024 * 1024,
    // Schema hard-ceiling, kept ABOVE sizeBytes on purpose: an over-limit
    // request (600 MB–1 GiB) then reaches the route's clean 413 instead of a
    // Zod parse error. The real per-upload gate is sizeBytes, checked in /sign.
    sizeBytesPremium: 1024 * 1024 * 1024,
    perMessage: 10,
    mimeAllowlist: [
      'image/png',
      'image/jpeg',
      'image/webp',
      'image/gif',
      'image/avif',
      'video/mp4',
      'video/webm',
      'audio/mpeg',
      'audio/ogg',
      // expo-audio HIGH_QUALITY preset records .m4a → audio/mp4 (mobile voice
      // notes). Browser MediaRecorder emits audio/webm below.
      'audio/mp4',
      // Browser MediaRecorder (Chrome/Firefox) emits audio/webm by
      // default for our hold-to-record voice messages; allow it
      // alongside the other audio mimes.
      'audio/webm',
      'audio/wav',
      'application/pdf',
      'text/plain',
      // Archives — community shares drivers / mod packs / logs as .zip etc.
      'application/zip',
      'application/x-zip-compressed',
      'application/x-7z-compressed',
      'application/vnd.rar',
      'application/x-rar-compressed',
      'application/gzip',
      'application/x-tar',
      // Office / data documents.
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/json',
      'text/csv',
      'text/markdown',
      // Catch-all for any other file the browser couldn't type. Attachments
      // are stored in object storage and only ever downloaded (never executed
      // server-side), and this is a closed friends community, so arbitrary
      // file sharing is intended.
      'application/octet-stream',
    ] as readonly string[],
  },
  dm: {
    /**
     * Max DM / group-DM channels included in the WS `ready` snapshot,
     * most-recently-active first. Protects the snapshot payload from an
     * account with an unbounded DM history; older conversations are still
     * reachable through REST.
     */
    readyMax: 200,
    /** Max length of a group-DM name. */
    groupNameMax: 80,
    /** Max OTHER participants when creating a group DM (excludes the creator). */
    groupUserIdsMax: 49,
  },
  readState: {
    /**
     * Unread / mention counts in a read state stop here. Clients show the cap
     * as "99+".
     */
    countCap: 100,
    /**
     * Newest messages after a read pointer the server looks at, before
     * dropping own messages, thread comments, service lines and deleted ones.
     * Bounds the snapshot cost however stale the pointer; mentions older than
     * this window are not counted.
     */
    scanWindow: 500,
  },
  poll: {
    questionMax: 200,
    optionMax: 80,
    optionsMin: 2,
    optionsMax: 10,
  },
  task: {
    titleMax: 120,
    labelMax: 120,
    itemsMin: 1,
    itemsMax: 30,
    /** A member re-ticking the same item within this window gets no second
     *  «done» service line — un-tick/tick flapping can't flood the chat. */
    doneNoticeCooldownMs: 10 * 60_000,
  },
  role: {
    nameMax: 100,
    /** Per-guild member nickname max. */
    nicknameMax: 100,
  },
  invite: {
    codeLength: 8,
    defaultTtlSeconds: 60 * 60 * 24 * 7,
    maxUsesDefault: null as number | null,
  },
  voice: {
    bitrateKbpsMin: 8,
    bitrateKbpsMax: 384,
    userLimitMax: 99,
  },
  user: {
    usernameMin: 2,
    usernameMax: 32,
    displayNameMax: 64,
    statusTextMax: 64,
    bioMax: 280,
  },
  followUp: {
    /** Cap on a single user's open + done follow-ups, so the inbox query and
     *  the reminder poller stay bounded. */
    perUser: 200,
    /** Optional free-text note attached to a follow-up ("спросить про сроки"). */
    noteMax: 280,
  },
  vaultShelf: {
    /** Max shelves ("полки") a user can create in their personal Сейф. */
    perUser: 100,
    /** Max length of a shelf name. */
    nameMax: 40,
    /** Max shelves a single saved item can be filed under (it's tags, so >1). */
    perMessage: 20,
  },
  report: {
    /** Optional free-text detail a reporter can add ("он скинул это в ЛС"). */
    noteMax: 1000,
    /** A moderator's resolution note when they action/dismiss a report. */
    resolutionMax: 1000,
    /** Anti-abuse: max reports a single user may file per rolling hour. */
    perReporterPerHour: 30,
    /** Page size for a moderation queue read. */
    queuePageSize: 50,
  },
  bugReport: {
    /** Beta feedback description bounds. */
    descriptionMin: 5,
    descriptionMax: 2000,
    /** Anti-abuse: max bug reports a single user may file per rolling hour. */
    perReporterPerHour: 20,
    /** Page size for the bug-report queue read. */
    queuePageSize: 50,
  },
  search: {
    /**
     * Shortest message-search query. The trigram index cannot serve shorter
     * patterns, so one or two letters read every message of every readable
     * channel; such queries answer empty instead of scanning.
     */
    minQueryLength: 3,
    /** Search requests per user per minute (each route). */
    perUserPerMinute: 30,
  },
});

export type Limits = typeof LIMITS;
